import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client";
import { AuthManagerContext, AuthStatus } from "../../contexts/AuthManager";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { UPDATE_PRIVATE_CLIENT_PROFILE } from "../../graphql/core";
import { useCurrentProfile } from "../../hooks/use-current-profile";

type EditorFields = {
    email: string;
};

type ValidationErrors = Record<keyof EditorFields, string>;

type PrivateClientEditorScreenProps = StackScreenProps<RootStackParamList, "PrivateClientEdit">;

const PrivateClientEditorScreen: React.FC<PrivateClientEditorScreenProps> = ({ navigation }) => {
    const { status, refreshUser } = React.useContext(AuthManagerContext);
    const currentProfile = useCurrentProfile();

    const [
        updatePrivateClientProfile,
        { error: savePrivateClientError, loading: privateClientSaving, reset: privateClientReset },
    ] = useMutation(UPDATE_PRIVATE_CLIENT_PROFILE);
    useApolloErrorSnackbar(savePrivateClientError, privateClientReset);

    const loading = status == AuthStatus.Loading;
    const i18n = useI18n();

    const [email, setEmail] = useState<string>(currentProfile?.businessEmail || "");

    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {} as ValidationErrors,
    );

    useEffect(() => {
        setEmail((oldEmail) => {
            if (currentProfile && oldEmail !== (currentProfile?.businessEmail || "")) {
                return currentProfile.businessEmail || "";
            }
            return oldEmail;
        });
    }, [currentProfile]);

    const validate = useCallback((): boolean => {
        const result: Partial<ValidationErrors> = {};

        if (!email) {
            result.email = i18n.t("screens.user.errors.email");
        }

        setValidationErrors(result as ValidationErrors);
        return Object.keys(result).length == 0;
    }, [email, i18n]);

    const save = React.useCallback(async () => {
        if (!validate()) {
            return;
        }
        try {
            await updatePrivateClientProfile({
                variables: {
                    changes: {
                        businessEmail: email,
                    },
                },
            });
            void refreshUser?.(); // do not await to avoid visual glitch
            navigation.goBack();
        } catch (ex) {
            //console.error(ex);
            // this is handled by saveError in useMutation
        }
    }, [validate, updatePrivateClientProfile, email, refreshUser, navigation]);

    const hasChanges = useMemo(() => {
        return currentProfile?.businessEmail !== email;
    }, [email, currentProfile?.businessEmail]);

    const hasError = useMemo(() => {
        return Object.keys(validationErrors).length !== 0;
    }, [validationErrors]);

    const disabled = loading || privateClientSaving;

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: save,
                    validating: privateClientSaving,
                    isValid: hasChanges && !hasError,
                },
            ]}
        >
            <ScrollView>
                <VStack v={1} gap={1} alignment="stretch" h="2/3">
                    <TextField
                        error={validationErrors.email}
                        value={email}
                        label={validationErrors.email || i18n.t("user.email")}
                        onChangeText={setEmail}
                        keyboardType="email-address"
                        textContentType="emailAddress"
                        autoComplete="email"
                        autoCorrect={false}
                        autoCapitalize="none"
                        disabled={disabled}
                    />
                </VStack>
            </ScrollView>
        </ScreenCard>
    );
};

export default PrivateClientEditorScreen;
